import { escapeCssSelector } from '@app/utils/domHelpers'

/**
 * Turbolinks replaces the DOM dynamically, which means selectors like :target might not work as expected while navigating on the same tab.
 * For this reason we need to manually scroll to the anchor element, adding an offset to the scroll position equal to the height of the fixed header.
 * After the appropriate CSS is applied, the element will be in the correct position.
 */
export const fixAnchorScrolling = (): void => {
  const hash = window.location.hash

  if (!hash) {
    return
  }

  // Check if the hash, after removing the '#', is a number or starts with a number
  // If it is, we have to return early as it is not a valid anchor
  const hashValue = hash.substring(1)
  if (/^\d+$/.test(hashValue) || /^\d/.test(hashValue)) {
    return
  }

  try {
    const sanitizedHash = escapeCssSelector(hashValue)
    const target = document.querySelector(`#${sanitizedHash}`) as HTMLElement

    if (target) {
      const headerHeight = getComputedStyle(
        document.documentElement
      ).getPropertyValue('--sticky-header-height')

      target.style.scrollSnapAlign = 'start'
      target.style.scrollMarginTop = headerHeight

      target.scrollIntoView({
        behavior: 'smooth',
      })
    }
  } catch (error) {
    console.error('Failed to scroll to the target element:', error)
  }
}

export default (): (() => void) => {
  const unsubscribe: Array<() => void> = []

  window.onDomLoad(($dom) => {
    const doc = $dom[0]

    doc.addEventListener('turbolinks:load', fixAnchorScrolling)

    unsubscribe.push((): void => {
      doc.removeEventListener('turbolinks:load', fixAnchorScrolling)
    })
  })

  return () => {
    unsubscribe.forEach((fn) => fn())
  }
}
